/**
 * Slab Quote Modal - Client-side logic for the shared "Request a quote" form
 *
 * @ui @slab-quote @shopify @product-page @modal
 *
 * FOUR quote kinds, read from #slab-quote-form[data-quote-kind] (owner rulings 2026-09-28):
 *   slab        the original slab page — freight + unloading questions, slab counts
 *   general     the universal quote-only page (2026-09-24) — free-text quantity, no freight questions
 *   field_tile  size / quantity + unit / application / project type / zip / needed-by
 *               (mosaics use this kind with the quantity unit "sheets")
 *   molding     linear footage / piece length / piece count / zip / needed-by
 * The kind is posted as `quote_kind`, and the kind-specific answers as the flat `quote_fields`
 * object. Everything else in the payload is unchanged from the original slab/general contract.
 * Contract: docs/plans/QUOTE_FORM_CONTRACT_2026-09-28.md
 *
 * Which fields a kind requires, and what goes into `quote_fields`, lives in
 * assets/quote-form-fields.js (window.SGIQuoteForms) — loaded just before this file by
 * snippets/slab-quote-modal.liquid. This file holds only what does NOT vary per kind.
 *
 * Handles: US state dropdown population, ship-to-type conditional fields (picking up at the
 * warehouse hides the delivery address + unloading-equipment fields; "Fabricator / business name"
 * is required only when shipping to a fabricator or a business with a loading dock), client-side
 * validation, JSON submission with endpoint failover, success/error views, and submit-button
 * loading state.
 *
 * Deployed to Shopify theme as assets/slab-quote.js. Loaded ONLY from
 * snippets/slab-quote-modal.liquid — not registered globally in layout/theme.liquid like
 * sample-order.js is.
 *
 * Fields are looked up by `[name="..."]` inside #slab-quote-form, never by id — the shared
 * input/select/checkbox snippets (snippets/input.liquid etc.) generate section/form-scoped
 * dynamic ids that aren't stable to hang JS off of.
 *
 * Endpoint strategy: the Shopify app-proxy path first (same origin, no CORS), then the backend's
 * direct /api/slab-quote mount on sgi.surfacegroup.com as a fallback — the same idiom as
 * assets/trade-program-v3-hybrid.js. A 400 from either host is a validation answer and is shown
 * as-is, never retried.
 *
 * See Also: slab-quote-modal.liquid (snippet), quote-form-fields.js (per-kind field table)
 */

(function () {
  'use strict';

  var ENDPOINTS = [
    '/apps/trade-program/slab-quote/submit',
    'https://sgi.surfacegroup.com/api/slab-quote/submit'
  ];

  var VALID_KINDS = ['slab', 'general', 'field_tile', 'molding'];

  // ========================================================================
  // Small DOM helpers (scoped to the form passed in)
  // ========================================================================

  function field(form, name) {
    return form.querySelector('[name="' + name + '"]');
  }

  function val(form, name) {
    var el = field(form, name);
    return el ? el.value.trim() : '';
  }

  function isChecked(form, name) {
    var el = field(form, name);
    return !!(el && el.checked);
  }

  function radioValue(form, name) {
    var nodes = form.querySelectorAll('[name="' + name + '"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].checked) return nodes[i].value;
    }
    return '';
  }

  function numberOrNull(raw) {
    if (raw === '' || raw === null || typeof raw === 'undefined') return null;
    var n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  function intOrNull(raw) {
    if (raw === '' || raw === null || typeof raw === 'undefined') return null;
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  function markInvalid(el) {
    if (el) el.classList.add('so-invalid');
  }

  function clearInvalid(form) {
    var invalids = form.querySelectorAll('.so-invalid');
    for (var i = 0; i < invalids.length; i++) invalids[i].classList.remove('so-invalid');
  }

  // ========================================================================
  // Quote kind + its field table (assets/quote-form-fields.js)
  // ========================================================================

  function quoteKind(form) {
    var kind = form.getAttribute('data-quote-kind') || form.getAttribute('data-quote-mode') || 'slab';
    return VALID_KINDS.indexOf(kind) === -1 ? 'general' : kind;
  }

  // Defensive default: if quote-form-fields.js failed to load, the form still submits as a
  // slab/general-shaped request rather than throwing.
  var FALLBACK_CONFIG = { full_ship: true, freight: false, requires: [], extras: function () { return {}; } };

  function configFor(form) {
    var api = window.SGIQuoteForms;
    if (!api || typeof api.formConfig !== 'function') return FALLBACK_CONFIG;
    return api.formConfig(quoteKind(form)) || FALLBACK_CONFIG;
  }

  // Accessor bag handed to a kind's extras() builder.
  function accessors(form) {
    return {
      val: function (name) { return val(form, name); },
      num: function (name) { return numberOrNull(val(form, name)); },
      int: function (name) { return intOrNull(val(form, name)); },
      checked: function (name) { return isChecked(form, name); },
      radio: function (name) { return radioValue(form, name); }
    };
  }

  // ========================================================================
  // Message area (#slab-quote-message) — mirrors snippets/banner.liquid classes
  // ========================================================================

  function showMessage(text, status) {
    var el = document.getElementById('slab-quote-message');
    if (!el) return;
    el.className = status ? 'banner banner--' + status : 'banner';
    el.textContent = text;
    el.hidden = false;
  }

  function hideMessage() {
    var el = document.getElementById('slab-quote-message');
    if (el) el.hidden = true;
  }

  // ========================================================================
  // Ship-to-type conditional fields (full-ship kinds only)
  // ========================================================================

  function initShipToType(form) {
    var addressWrap = document.getElementById('slab-quote-address-fields');
    var businessNameInput = field(form, 'delivery_business_name');
    var radios = form.querySelectorAll('[name="delivery_ship_to_type"]');
    if (!radios.length) return;

    function update() {
      var value = radioValue(form, 'delivery_ship_to_type');
      var isPickup = value === 'pickup';

      if (addressWrap) addressWrap.hidden = isPickup;

      if (businessNameInput) {
        businessNameInput.required = !isPickup && (value === 'fabricator' || value === 'business_dock');
      }
    }

    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', update);
    }
    update();
  }

  // ========================================================================
  // Validation — mirrors sample-order.js's array-of-messages approach; the
  // form carries novalidate, so this is the only thing gating submission.
  // ========================================================================

  function validateForm(form) {
    var errors = [];
    var kind = quoteKind(form);
    var config = configFor(form);
    clearInvalid(form);

    function require(name, message) {
      if (!val(form, name)) {
        errors.push(message);
        markInvalid(field(form, name));
      }
    }

    require('first_name', 'First name is required');
    require('last_name', 'Last name is required');
    require('email', 'Email is required');
    require('phone', 'Phone is required');
    require('customer_type', 'Customer type is required');

    // Kind-specific required fields (quote-form-fields.js)
    for (var r = 0; r < config.requires.length; r++) {
      require(config.requires[r][0], config.requires[r][1]);
    }

    // Slab page: at least one of slab_count / sqft_needed
    if (kind === 'slab' && !val(form, 'slab_count') && !val(form, 'sqft_needed')) {
      errors.push('Enter a number of slabs or an approximate square footage');
      markInvalid(field(form, 'slab_count'));
      markInvalid(field(form, 'sqft_needed'));
    }

    // Email format — reject double TLDs like .com.com, require valid structure
    var email = val(form, 'email');
    if (email) {
      if (!/^[^\s@]+@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,10}$/.test(email)) {
        errors.push('Please enter a valid email address');
        markInvalid(field(form, 'email'));
      } else {
        var domain = email.split('@')[1];
        if (/\.([a-z]{2,})\.\1$/i.test(domain)) {
          errors.push('Email appears to have a duplicated domain (e.g. .com.com)');
          markInvalid(field(form, 'email'));
        }
      }
    }

    // Phone — at least 10 digits
    var phone = val(form, 'phone');
    if (phone) {
      var digits = phone.replace(/\D/g, '');
      if (digits.length < 10) {
        errors.push('Please enter a valid phone number (at least 10 digits)');
        markInvalid(field(form, 'phone'));
      }
    }

    validateDelivery(form, kind, config, errors, require);

    return errors;
  }

  function validateDelivery(form, kind, config, errors, require) {
    var shipTo = radioValue(form, 'delivery_ship_to_type');

    if (config.full_ship) {
      require('timeline', 'Timeline is required');

      if (!shipTo) {
        errors.push(kind === 'slab' ? 'Please tell us where the slab is going' : 'Please tell us where the order is going');
      }

      // Delivery address + equipment — only required while visible (not pickup)
      if (shipTo !== 'pickup') {
        require('delivery_address1', 'Delivery address is required');
        require('delivery_city', 'Delivery city is required');
        require('delivery_state', 'Delivery state is required');

        if (config.freight) {
          if (!radioValue(form, 'delivery_unloading_equipment')) {
            errors.push('Please select the unloading equipment available on site');
          }
          require('delivery_truck_access', 'Truck access is required');
        }

        if ((shipTo === 'fabricator' || shipTo === 'business_dock') && !val(form, 'delivery_business_name')) {
          errors.push(config.freight ? 'Fabricator / business name is required for this delivery type' : 'Business name is required for this delivery type');
          markInvalid(field(form, 'delivery_business_name'));
        }
      }
    }

    // Zip: always required except when the customer is picking the order up themselves.
    if (shipTo !== 'pickup') {
      require('delivery_postal_code', 'Delivery postal code is required');
      var postal = val(form, 'delivery_postal_code');
      if (postal && !/^\d{5}(-\d{4})?$/.test(postal)) {
        errors.push('Please enter a valid US zip code (e.g. 60010 or 60010-1234)');
        markInvalid(field(form, 'delivery_postal_code'));
      }
    }
  }

  // ========================================================================
  // Payload — shape is a fixed backend contract:
  // docs/plans/QUOTE_FORM_CONTRACT_2026-09-28.md
  // The top-level keys below are IDENTICAL to the original slab/general
  // payload; only `quote_kind` widens and `quote_fields` is new.
  // ========================================================================

  function buildPayload(form) {
    var config = configFor(form);

    return {
      store_id: val(form, 'store_id') || 'sgi',
      website: val(form, 'website'),
      first_name: val(form, 'first_name'),
      last_name: val(form, 'last_name'),
      email: val(form, 'email'),
      phone: val(form, 'phone'),
      company: val(form, 'company'),
      customer_type: val(form, 'customer_type'),
      product: {
        product_title: val(form, 'product_title'),
        product_handle: val(form, 'product_handle'),
        product_id: val(form, 'product_id') || null,
        variant_id: val(form, 'variant_id') || null,
        product_sku: val(form, 'product_sku') || null,
        product_vendor: val(form, 'product_vendor') || null,
        product_image_url: val(form, 'product_image_url') || null,
        size_text: val(form, 'size_text') || null,
        sqft_per_slab: numberOrNull(val(form, 'sqft_per_slab')),
        price_per_slab: null  // prices are quoted, never sent from the page (owner 2026-09-14)
      },
      quote_kind: quoteKind(form),
      quote_fields: config.extras(accessors(form)),
      quantity_text: val(form, 'quantity_text') || null,
      slab_count: intOrNull(val(form, 'slab_count')),
      sqft_needed: numberOrNull(val(form, 'sqft_needed')),
      need_photos: isChecked(form, 'need_photos'),
      project_description: val(form, 'project_description'),
      timeline: val(form, 'timeline'),
      delivery: {
        ship_to_type: radioValue(form, 'delivery_ship_to_type'),
        business_name: val(form, 'delivery_business_name'),
        contact_name: val(form, 'delivery_contact_name'),
        contact_phone: val(form, 'delivery_contact_phone'),
        address1: val(form, 'delivery_address1'),
        address2: val(form, 'delivery_address2'),
        city: val(form, 'delivery_city'),
        state: val(form, 'delivery_state'),
        postal_code: val(form, 'delivery_postal_code'),
        country: val(form, 'delivery_country') || 'US',
        receiving_hours: val(form, 'delivery_receiving_hours'),
        unloading_equipment: radioValue(form, 'delivery_unloading_equipment'),
        truck_access: val(form, 'delivery_truck_access'),
        notes: val(form, 'delivery_notes')
      },
      subscribe_updates: isChecked(form, 'subscribe_updates')
    };
  }

  // ========================================================================
  // Submission with endpoint failover — see module header for why this
  // mirrors trade-program-v3-hybrid.js rather than sample-order.js.
  // A 400 is a validation error (same on every endpoint) and stops the loop
  // immediately instead of retrying; anything else (network failure, 404 if
  // the app proxy route isn't configured yet, 5xx) tries the next endpoint.
  // ========================================================================

  function submitWithFailover(payload) {
    var body = JSON.stringify(payload);
    var index = 0;

    function parseJsonSafe(resp) {
      return resp.json().catch(function () { return {}; });
    }

    function attempt() {
      var endpoint = ENDPOINTS[index++];

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).then(function (resp) {
        if (resp.ok) return resp.json();

        return parseJsonSafe(resp).then(function (data) {
          if (resp.status === 400) {
            throw new Error(data.detail || 'Please check the form and try again.');
          }
          if (index < ENDPOINTS.length) return attempt();
          throw new Error(data.detail || 'Submission failed. Please try again.');
        });
      }, function () {
        // fetch() rejected outright — offline, CORS, or proxy not configured
        if (index < ENDPOINTS.length) return attempt();
        throw new Error('Unable to reach the server. Please try again or email order@surfacegroup.com.');
      });
    }

    return attempt();
  }

  // ========================================================================
  // Success / submit handling
  // ========================================================================

  function showSuccess(data) {
    var form = document.getElementById('slab-quote-form');
    var success = document.getElementById('slab-quote-success');
    var numberEl = document.getElementById('slab-quote-number');

    if (numberEl) numberEl.textContent = (data && data.quote_number) || '';
    if (form) form.hidden = true;
    if (success) success.hidden = false;
  }

  function setLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    if (loading) {
      button.dataset.originalText = button.dataset.originalText || button.textContent;
      button.textContent = 'Sending...';
    } else if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    var form = event.target;
    var button = form.querySelector('button[type="submit"]');

    hideMessage();

    var errors = validateForm(form);
    if (errors.length > 0) {
      showMessage(errors.join('. '), 'error');
      return false;
    }

    setLoading(button, true);

    submitWithFailover(buildPayload(form))
      .then(function (data) {
        showSuccess(data);
      })
      .catch(function (err) {
        showMessage((err && err.message) || 'An error occurred. Please try again.', 'error');
        setLoading(button, false);
      });

    return false;
  }

  // ========================================================================
  // Init
  // ========================================================================

  function init() {
    var form = document.getElementById('slab-quote-form');
    var modal = document.getElementById('slab-quote-modal');
    if (!form || !modal) return;

    var api = window.SGIQuoteForms;
    var stateSelect = field(form, 'delivery_state');
    if (stateSelect && api && typeof api.populateStateSelect === 'function') {
      var statePrefill = document.getElementById('slab-quote-state-prefill');
      api.populateStateSelect(stateSelect, statePrefill ? statePrefill.value : '');
    }

    initShipToType(form);

    form.addEventListener('submit', onSubmit);

    var successView = document.getElementById('slab-quote-success');
    var closeButton = successView ? successView.querySelector('button') : null;
    if (closeButton) {
      closeButton.addEventListener('click', function () {
        if (typeof modal.hide === 'function') modal.hide();
      });
    }

    // Re-opening after a previous submission should show the form again, not
    // the stale "request received" view (mirrors sample-order.js's reset-on-open).
    modal.addEventListener('dialog:after-show', function () {
      if (successView) successView.hidden = true;
      form.hidden = false;
      hideMessage();
      clearInvalid(form);
      setLoading(form.querySelector('button[type="submit"]'), false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
